import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { TitleStrategy, RouterStateSnapshot, ActivatedRouteSnapshot } from '@angular/router';
import { TitleStateService } from './title-state.service';

@Injectable({ providedIn: 'root' })
export class CustomTitleStrategy extends TitleStrategy {
    appName = 'Nova';
    routerStateSnapshot: RouterStateSnapshot;

    constructor(
        private readonly _titleService: Title,
        private readonly _titleStateService: TitleStateService
    ) {
        super();

        this._titleStateService.title$.subscribe((title) => {
            if (title && title.length > 0) {
                this.updateTitle(this.routerStateSnapshot, title);
            }
        });
    }

    override updateTitle(routerState: RouterStateSnapshot, customTitle: string = ''): void {
        let title: string = null;
        this.routerStateSnapshot = routerState;

        try {
            title = this.getPageTitle(routerState, customTitle);
        } catch (error) {
            console.error('Error setting page title', error);
        } finally {
            this._titleService.setTitle(title ?? this.appName);
        }
    }

    /*
     * Get page title from route data
     * If route has multiple data, get the last one
     * If route has a parameter and its numeric, append it to the title
     * ex. 'Job ID: 1234 - Nova'
     */
    private getPageTitle(routerState: RouterStateSnapshot, customTitle: string = ''): string {
        let title = this.buildTitle(routerState);

        // if title is not found in route, return 'Nova'
        if (!title) {
            return this.appName;
        }

        let titleDetail = this.getRouteData(routerState);
        const titleDetailResolverPath: string = titleDetail['titleDetailResolverPath'];
        if (titleDetailResolverPath) {
            titleDetailResolverPath?.split('.')?.forEach((path) => (titleDetail = titleDetail?.[path]));
        }

        // check and see if route has a parameter ex. Job ID
        // title should be in this format 'Job ID' so that the page title would be 'Job ID: 1234 - Nova'
        const routeParam = this.getLastRouteParam(routerState);

        const titleAddons: string[] = customTitle && customTitle.length > 0
            ? [customTitle]
            : [routeParam, titleDetailResolverPath && titleDetail ? titleDetail : null].filter((x) => x);

        if (titleAddons.length) {
            title += `: ${titleAddons.join(' ')} - ${this.appName}`;
        } else {
            title = `${title} - ${this.appName}`;
        }

        return title;
    }

    /*
     * get page title param from route data. if page has multiple params, get the LAST one ONLY
     * if param is not numeric, return null
     * make sure your page title reflects on the last param
     * ex. /facilities/1234/jobs/999 - then page title would be 'Job ID: 999 - Nova'
     */
    private getLastRouteParam(state: RouterStateSnapshot): string | null {
        let route: ActivatedRouteSnapshot | null = state.root;

        while (route?.firstChild) {
            route = route.firstChild;
        }

        // get last param if param[s] exist
        if (route?.paramMap.keys.length) {
            let param: string | null = null;
            const paramKeys = route.paramMap.keys;
            param = route.paramMap.get(paramKeys[paramKeys.length - 1]) ?? null; // extract last param

            // currently, we only need the last param if it's a number
            return param && !isNaN(Number(param)) ? param : null;
        }

        return null;
    }

    private getRouteData(routerStateSnapshot: RouterStateSnapshot): any {
        let route = routerStateSnapshot.root;
        while (route.firstChild) {
            route = route.firstChild;
        }
        return route.data;
    }
}
